<script setup>
import { computed } from 'vue'
import AppLayout from '@/layouts/AppLayout.vue'
import PdfButton from '@/components/PdfButton.vue'

const METEO_LABELS = {
  'wi-day-sunny':'Ensoleillé','wi-cloudy':'Nuageux','wi-cloudy-gusts':'Rafales',
  'wi-cloudy-windy':'Venteux/nuageux','wi-fog':'Brouillard','wi-sprinkle':'Bruine',
  'wi-showers':'Averses','wi-rain':'Pluie','wi-rain-wind':'Pluie et vent',
  'wi-storm-showers':'Orages/averses','wi-thunderstorm':'Orage','wi-rain-mix':'Pluie mixte',
  'wi-sleet':'Grésil','wi-snow':'Neige','wi-snow-wind':'Neige et vent','wi-hail':'Grêle',
  'wi-windy':'Venteux','wi-strong-wind':'Grand vent','wi-hot':'Chaud',
  'wi-snowflake-cold':'Froid','wi-raindrop':'Goutte de pluie',
}
import { Pie } from 'vue-chartjs'
import { Chart as ChartJS, ArcElement, Tooltip, Legend } from 'chart.js'
ChartJS.register(ArcElement, Tooltip, Legend)

const props = defineProps({
  rapport:               Object,
  projet:                Object,
  estimation:            Object,
  sommaire:              Object,
  elements:              Array,
  personnels:            Object,
  equipements:           Object,
  inventaires:           Object,
  parties_suivies:       Object,
  liaisons:              Array,
  liaisons_texte:        Array,
  detail_statistiques:   Array,
  can_see_costs:         Boolean,
  can_see_costs_personnel: Boolean,
  can_see_costs_inventaire: Boolean,
  can_update:            Boolean,
  can_delete:            Boolean,
  show_meteo:            Boolean,
  show_camionneurs:      Boolean,
  show_inventaire:       Boolean,
  show_partie_suivies:   Boolean,
  show_liaisons:         Boolean,
  compilation_url:       String,
})

// ── Formatters ──────────────────────────────────────────────────────────────
function fmtCurrency(val) {
  if (val === null || val === undefined) return '—'
  return new Intl.NumberFormat('fr-CA', { style: 'currency', currency: 'CAD', minimumFractionDigits: 2 }).format(val)
}
function fmtNeg(val) {
  if (val === null || val === undefined) return '—'
  const f = fmtCurrency(Math.abs(val))
  return val < 0 ? `(${f})` : f
}
function fmtBalance(val) {
  if (val === null || val === undefined) return '—'
  const f = fmtCurrency(Math.abs(val))
  return val < 0 ? `(${f})` : f
}
function fmtQty(val) {
  if (val === null || val === undefined) return '—'
  return Number(val).toFixed(1)
}
function balanceCls(balance) {
  return (balance ?? 0) >= 0 ? 'text-tertiary' : 'text-error'
}

// ── Pie chart ────────────────────────────────────────────────────────────────
const pieData = computed(() => ({
  labels: ['Coûts Équipements', 'Coûts Personnel', 'Valeur Exécutée'],
  datasets: [{
    data: [props.sommaire?.couts_equipement || 0, props.sommaire?.couts_personnel || 0, props.sommaire?.valeur_execute || 0],
    backgroundColor: ['rgba(99,102,241,0.8)', 'rgba(52,211,153,0.8)', 'rgba(251,191,36,0.8)'],
    borderColor:     ['rgba(99,102,241,1)',   'rgba(52,211,153,1)',   'rgba(251,191,36,1)'],
    borderWidth: 1,
  }],
}))
const pieOptions = {
  responsive: true,
  plugins: {
    legend: { position: 'bottom', labels: { color: '#94a3b8', font: { size: 11 } } },
    tooltip: { callbacks: { label: ctx => ' ' + fmtCurrency(ctx.raw) } },
  },
}

// ── Colspans ─────────────────────────────────────────────────────────────────
const pColBase = computed(() => {
  let n = 2
  if (props.show_camionneurs) n += 3
  if (props.can_see_costs_personnel) n += 1
  return n
})
const pColFull = computed(() => pColBase.value + 2 + (props.can_see_costs_personnel ? 1 : 0))
const eColBase = computed(() => 2 + (props.can_see_costs_personnel ? 1 : 0))
const eColFull = computed(() => eColBase.value + 1 + (props.can_see_costs_personnel ? 1 : 0))
</script>

<template>
  <AppLayout>
    <div class="p-8 bg-background min-h-screen">

      <!-- ── Topbar ──────────────────────────────────────────────────────── -->
      <div class="flex items-start justify-between mb-8">
        <div>
          <div class="flex items-center gap-1.5 text-xs text-outline mb-1 flex-wrap">
            <a :href="projet.url" class="font-mono font-bold text-primary hover:brightness-125">{{ projet.code }}</a>
            <span class="material-symbols-outlined text-xs">chevron_right</span>
            <a :href="estimation.url" class="font-mono text-on-surface hover:text-primary">{{ estimation.code }}</a>
            <span class="material-symbols-outlined text-xs">chevron_right</span>
            <span class="text-outline">Rapport {{ rapport.date }}</span>
          </div>
          <h1 class="text-2xl font-black text-on-surface">Rapport du {{ rapport.date }}</h1>
          <p class="text-sm text-outline mt-0.5">{{ rapport.utilisateur }}</p>
        </div>
        <div class="flex items-center gap-2">
          <a :href="compilation_url"
            class="px-4 py-2.5 text-xs font-black uppercase tracking-widest rounded-md text-outline hover:text-on-surface bg-surface-container-low border border-outline-variant/10 transition-all flex items-center gap-1.5">
            <span class="material-symbols-outlined text-sm">summarize</span>Compilation
          </a>
          <a :href="rapport.xlsx_url"
            class="px-4 py-2.5 text-xs font-black uppercase tracking-widest rounded-md text-outline hover:text-on-surface bg-surface-container-low border border-outline-variant/10 transition-all flex items-center gap-1.5">
            <span class="material-symbols-outlined text-sm">download</span>XLSX
          </a>
          <PdfButton :url="rapport.pdf_url" />
          <a v-if="can_update" :href="rapport.edit_url"
            class="machined-sheen px-5 py-2.5 rounded-md text-on-primary font-black text-xs uppercase tracking-widest flex items-center gap-2 hover:brightness-110 transition-all shadow-lg">
            <span class="material-symbols-outlined text-sm">edit_note</span>Modifier
          </a>
          <a v-if="rapport.audit_url" :href="rapport.audit_url"
            class="px-4 py-2.5 text-xs font-black uppercase tracking-widest rounded-md text-outline hover:text-on-surface bg-surface-container-low border border-outline-variant/10 transition-all flex items-center gap-1.5">
            <span class="material-symbols-outlined text-sm">history</span>Historique
          </a>
        </div>
      </div>

      <!-- ── Info Card + Camembert ──────────────────────────────────────── -->
      <div class="grid grid-cols-2 gap-5 mb-6">

        <!-- Info Card -->
        <div class="bg-surface-container rounded-xl border border-outline-variant/10 p-6 space-y-3">
          <div class="flex items-center gap-3">
            <span class="text-[10px] font-black uppercase tracking-widest text-outline w-28 shrink-0">Projet</span>
            <a :href="projet.url" class="font-mono font-bold text-primary hover:brightness-125">{{ projet.code }}</a>
          </div>
          <div class="flex items-center gap-3">
            <span class="text-[10px] font-black uppercase tracking-widest text-outline w-28 shrink-0">Estimation</span>
            <a :href="estimation.url" class="font-mono text-sm text-on-surface hover:text-primary">{{ estimation.code }}</a>
          </div>
          <div class="flex items-center gap-3">
            <span class="text-[10px] font-black uppercase tracking-widest text-outline w-28 shrink-0">Date</span>
            <span class="font-mono text-sm text-on-surface">{{ rapport.date }}</span>
          </div>
          <div class="flex items-center gap-3">
            <span class="text-[10px] font-black uppercase tracking-widest text-outline w-28 shrink-0">Contremaitre</span>
            <span class="text-sm text-on-surface">{{ rapport.utilisateur }}</span>
          </div>
          <div v-if="show_meteo && rapport.meteo_label" class="flex items-center gap-3">
            <span class="text-[10px] font-black uppercase tracking-widest text-outline w-28 shrink-0">Météo</span>
            <span class="flex items-center gap-2 text-sm text-on-surface">
              <i :class="`wi ${rapport.meteo_label}`" style="font-size:1.2em"></i>
              {{ METEO_LABELS[rapport.meteo_label] ?? rapport.meteo_label }}
            </span>
          </div>
          <div v-for="ch in rapport.chainages" :key="ch.description" class="flex items-center gap-3">
            <span class="text-[10px] font-black uppercase tracking-widest text-outline w-28 shrink-0">{{ ch.description }}</span>
            <span class="font-mono text-sm text-on-surface">
              {{ fmtQty(ch.champ1) }}<template v-if="ch.champ2"> → {{ fmtQty(ch.champ2) }}</template>
              <span class="text-outline ml-1">{{ ch.unite }}</span>
            </span>
          </div>
        </div>

        <!-- Camembert -->
        <div v-if="can_see_costs && sommaire" class="bg-surface-container rounded-xl border border-outline-variant/10 p-6 flex flex-col">
          <h2 class="text-[11px] font-black uppercase tracking-widest text-outline mb-4">Répartition des coûts</h2>
          <div class="flex-1 flex items-center justify-center">
            <div class="w-full max-w-[220px]">
              <Pie :data="pieData" :options="pieOptions" />
            </div>
          </div>
        </div>
        <div v-else class="bg-surface-container rounded-xl border border-outline-variant/10 p-6"></div>

      </div>

      <!-- ── KPI Sommaire ────────────────────────────────────────────────── -->
      <div v-if="can_see_costs && sommaire" class="grid grid-cols-5 gap-4 mb-8">
        <div v-for="kpi in [
          { icon: 'account_balance', color: 'primary',  label: 'Valeur Globale',    val: sommaire.valeur_execute_total, fmt: fmtCurrency },
          { icon: 'check_circle',    color: 'tertiary', label: 'Valeur Exécutée',   val: sommaire.valeur_execute,       fmt: fmtCurrency },
          { icon: 'group',           color: 'outline',  label: 'Coûts Personnel',   val: sommaire.couts_personnel,      fmt: fmtNeg },
          { icon: 'construction',    color: 'outline',  label: 'Coûts Équipements', val: sommaire.couts_equipement,     fmt: fmtNeg },
          { icon: 'balance', color: (sommaire.productivite >= 0 ? 'tertiary' : 'error'), label: 'Solde Net', val: sommaire.productivite, fmt: fmtBalance },
        ]" :key="kpi.label"
          class="bg-surface-container-low border border-outline-variant/10 p-5 rounded-xl flex items-center gap-4">
          <div :class="['w-11 h-11 rounded-lg flex items-center justify-center shrink-0', `bg-${kpi.color}/10`]">
            <span :class="['material-symbols-outlined text-xl', `text-${kpi.color}`]">{{ kpi.icon }}</span>
          </div>
          <div class="min-w-0">
            <div class="text-[10px] font-black uppercase tracking-widest text-outline mb-0.5 truncate">{{ kpi.label }}</div>
            <div :class="['text-base font-black font-mono truncate', kpi.label === 'Solde Net' ? `text-${kpi.color}` : 'text-on-surface']">
              {{ kpi.fmt(kpi.val) }}
            </div>
          </div>
        </div>
      </div>

      <!-- ── Détail statistiques par TypePartieEstimation ──────────────── -->
      <div v-if="can_see_costs && detail_statistiques && detail_statistiques.length" class="mb-5">
        <div class="bg-surface-container rounded-xl overflow-hidden shadow-xl border border-outline-variant/10">
          <div class="px-6 py-4 bg-surface-container-high/60 border-b border-outline-variant/15">
            <h2 class="text-[11px] font-black uppercase tracking-widest text-on-surface">Détail statistiques</h2>
          </div>
          <table class="w-full text-left border-collapse text-xs">
            <thead>
              <tr class="border-b border-outline-variant/15">
                <th class="px-6 py-3 text-[10px] font-black uppercase tracking-widest text-outline"></th>
                <th v-for="col in detail_statistiques" :key="col.code"
                  class="px-6 py-3 text-[10px] font-black uppercase tracking-widest text-outline text-right">
                  {{ col.code }}
                </th>
              </tr>
            </thead>
            <tbody class="divide-y divide-outline-variant/10">
              <tr class="hover:bg-surface-container-highest/20 transition-colors">
                <th class="px-6 py-3 text-[10px] font-black uppercase tracking-widest text-outline">Estimation</th>
                <td v-for="col in detail_statistiques" :key="'e'+col.code"
                  class="px-6 py-3 text-right font-mono text-sm text-on-surface">{{ fmtCurrency(col.estimation) }}</td>
              </tr>
              <tr class="hover:bg-surface-container-highest/20 transition-colors">
                <th class="px-6 py-3 text-[10px] font-black uppercase tracking-widest text-outline">Coûts</th>
                <td v-for="col in detail_statistiques" :key="'c'+col.code"
                  class="px-6 py-3 text-right font-mono text-sm text-on-surface">{{ fmtNeg(col.couts) }}</td>
              </tr>
              <tr class="bg-surface-container-highest/40 font-bold">
                <th class="px-6 py-3 text-[10px] font-black uppercase tracking-widest text-outline">Net</th>
                <td v-for="col in detail_statistiques" :key="'n'+col.code"
                  :class="['px-6 py-3 text-right font-mono text-sm font-black', balanceCls(col.net)]">{{ fmtBalance(col.net) }}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      <!-- ── Chainages ───────────────────────────────────────────────────── -->
      <div v-if="rapport.chainages" class="mb-5">
        <div class="bg-surface-container rounded-xl overflow-hidden shadow-xl border border-outline-variant/10">
          <div class="px-6 py-4 bg-surface-container-high/60 border-b border-outline-variant/15 flex items-center justify-between gap-4">
            <h2 class="text-[11px] font-black uppercase tracking-widest text-on-surface">Chaînage</h2>
            <a v-if="can_update" :href="rapport.edit_chainage_url"
               class="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold rounded-lg bg-primary/10 text-primary hover:bg-primary/20 transition-colors">
              <span class="material-symbols-outlined text-sm">edit</span>Modifier
            </a>
          </div>
          <template v-if="rapport.chainages.length">
            <table class="w-full text-left border-collapse text-xs">
              <tbody class="divide-y divide-outline-variant/10">
                <tr v-for="ch in rapport.chainages" :key="ch.description" class="hover:bg-surface-container-highest/20 transition-colors">
                  <th class="px-6 py-3 text-[10px] font-black uppercase tracking-widest text-outline w-48">{{ ch.description }}</th>
                  <td class="px-6 py-3 font-mono text-sm text-on-surface">
                    De {{ fmtQty(ch.champ1) }}<template v-if="ch.champ2"> à {{ fmtQty(ch.champ2) }}</template>
                    <span class="text-outline ml-1">{{ ch.unite }}</span>
                  </td>
                </tr>
              </tbody>
            </table>
          </template>
          <p v-else class="px-6 py-4 text-sm text-outline italic">Aucun chainage n'est inscrit</p>
        </div>
      </div>

      <!-- ── Éléments par tag ────────────────────────────────────────────── -->
      <div v-for="(group, gi) in elements" :key="gi" class="mb-5">
        <div class="bg-surface-container rounded-xl overflow-hidden shadow-xl border border-outline-variant/10">
          <div class="px-6 py-4 bg-surface-container-high/60 border-b border-outline-variant/15 flex items-center justify-between">
            <h2 class="text-[11px] font-black uppercase tracking-widest text-on-surface">{{ group.tag || 'Éléments' }}</h2>
            <span v-if="can_see_costs_inventaire && group.total_valeur_execute" class="font-mono font-bold text-sm text-primary">
              {{ fmtCurrency(group.total_valeur_execute) }}
            </span>
          </div>
          <div v-if="!group.elements.length" class="px-6 py-4 text-sm text-outline italic">Aucun élément</div>
          <table v-else class="w-full text-left border-collapse">
            <thead>
              <tr class="border-b border-outline-variant/15">
                <th class="px-6 py-3 text-[10px] font-black uppercase tracking-widest text-outline w-32">Code</th>
                <th class="px-6 py-3 text-[10px] font-black uppercase tracking-widest text-outline">Description</th>
                <th class="px-6 py-3 text-[10px] font-black uppercase tracking-widest text-outline text-right w-24">Qté</th>
                <th class="px-6 py-3 text-[10px] font-black uppercase tracking-widest text-outline text-right w-24">Somme</th>
                <th class="px-6 py-3 text-[10px] font-black uppercase tracking-widest text-outline text-right w-24">Balance</th>
                <th class="px-6 py-3 text-[10px] font-black uppercase tracking-widest text-outline w-16">Unité</th>
                <th v-if="can_see_costs_inventaire" class="px-6 py-3 text-[10px] font-black uppercase tracking-widest text-outline text-right w-32">Valeur exéc.</th>
              </tr>
            </thead>
            <tbody class="divide-y divide-outline-variant/10">
              <tr v-for="(el, ei) in group.elements" :key="ei"
                :class="['hover:bg-surface-container-highest/20 transition-colors', ei % 2 === 1 ? 'bg-surface-container-low/20' : '']">
                <td class="px-6 py-3"><span class="font-mono text-sm font-bold text-primary">{{ el.code }}</span></td>
                <td class="px-6 py-3 text-sm text-on-surface">{{ el.description }}</td>
                <td class="px-6 py-3 text-right font-mono text-sm text-on-surface">{{ fmtQty(el.quantite) }}</td>
                <td class="px-6 py-3 text-right">
                  <span :class="['font-mono text-sm font-bold', balanceCls(el.quantite_balance)]">{{ fmtQty(el.quantite_somme) }}</span>
                </td>
                <td class="px-6 py-3 text-right">
                  <span :class="['font-mono text-sm font-bold', balanceCls(el.quantite_balance)]">{{ fmtQty(el.quantite_balance) }}</span>
                </td>
                <td class="px-6 py-3"><span class="text-[10px] font-bold uppercase text-outline">{{ el.type_unite }}</span></td>
                <td v-if="can_see_costs_inventaire" class="px-6 py-3 text-right font-mono text-sm text-on-surface">{{ fmtCurrency(el.valeur_execute) }}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      <!-- ── Personnel + Équipements ─────────────────────────────────────── -->
      <div class="grid grid-cols-2 gap-5 mb-5">

        <!-- Personnel -->
        <div class="bg-surface-container rounded-xl overflow-hidden shadow-xl border border-outline-variant/10">
          <div class="px-6 py-4 bg-surface-container-high/60 border-b border-outline-variant/15">
            <h2 class="text-[11px] font-black uppercase tracking-widest text-on-surface">Personnel</h2>
          </div>
          <div v-if="!personnels.personnels.length && !personnels.additionnels.length && !personnels.externes.length"
            class="px-6 py-4 text-sm text-outline italic">Aucun personnel</div>
          <table v-else class="w-full text-left border-collapse text-xs">
            <thead>
              <tr class="border-b border-outline-variant/15">
                <th class="px-4 py-3 text-[10px] font-black uppercase tracking-widest text-outline">Code</th>
                <th class="px-4 py-3 text-[10px] font-black uppercase tracking-widest text-outline">Nom</th>
                <template v-if="show_camionneurs">
                  <th class="px-4 py-3 text-[10px] font-black uppercase tracking-widest text-outline text-center w-9">AM</th>
                  <th class="px-4 py-3 text-[10px] font-black uppercase tracking-widest text-outline text-center w-9">PM</th>
                  <th class="px-4 py-3 text-[10px] font-black uppercase tracking-widest text-outline text-center w-12">Insp.</th>
                </template>
                <th v-if="can_see_costs_personnel" class="px-4 py-3 text-[10px] font-black uppercase tracking-widest text-outline text-right w-20">Taux</th>
                <th class="px-4 py-3 text-[10px] font-black uppercase tracking-widest text-outline text-right w-14">Rég.</th>
                <th class="px-4 py-3 text-[10px] font-black uppercase tracking-widest text-outline text-right w-14">Supp.</th>
                <th v-if="can_see_costs_personnel" class="px-4 py-3 text-[10px] font-black uppercase tracking-widest text-outline text-right w-24">Coût</th>
              </tr>
            </thead>
            <tbody class="divide-y divide-outline-variant/10">
              <tr v-for="(p, pi) in personnels.personnels" :key="'p'+pi" class="hover:bg-surface-container-highest/20 transition-colors">
                <td class="px-4 py-2"><span class="font-mono font-bold text-primary">{{ p.code }}</span></td>
                <td class="px-4 py-2 text-on-surface">{{ p.nom }}</td>
                <template v-if="show_camionneurs">
                  <td class="px-4 py-2 text-center"><span v-if="p.am" class="material-symbols-outlined text-xs text-tertiary">check</span><span v-else class="text-outline">—</span></td>
                  <td class="px-4 py-2 text-center"><span v-if="p.pm" class="material-symbols-outlined text-xs text-tertiary">check</span><span v-else class="text-outline">—</span></td>
                  <td class="px-4 py-2 text-center"><span v-if="p.inspection" class="material-symbols-outlined text-xs text-tertiary">check</span><span v-else class="text-outline">—</span></td>
                </template>
                <td v-if="can_see_costs_personnel" class="px-4 py-2 text-right font-mono text-on-surface">{{ fmtCurrency(p.taux_horaire) }}</td>
                <td class="px-4 py-2 text-right font-mono text-on-surface">{{ fmtQty(p.quantite_regulier) }}</td>
                <td class="px-4 py-2 text-right font-mono text-on-surface">{{ fmtQty(p.quantite_supplementaire) }}</td>
                <td v-if="can_see_costs_personnel" class="px-4 py-2 text-right font-mono text-on-surface">{{ fmtCurrency(p.quantite_taux) }}</td>
              </tr>
              <tr v-if="personnels.personnels.length" class="bg-surface-container-highest/40 border-t border-outline-variant/20">
                <td :colspan="pColBase" class="px-4 py-1.5 text-[10px] font-black uppercase tracking-widest text-outline text-right">Sous-total</td>
                <td class="px-4 py-1.5 text-right font-mono font-bold text-on-surface">{{ fmtQty(personnels.sommes.regulier) }}</td>
                <td class="px-4 py-1.5 text-right font-mono font-bold text-on-surface">{{ fmtQty(personnels.sommes.supplementaire) }}</td>
                <td v-if="can_see_costs_personnel" class="px-4 py-1.5 text-right font-mono font-bold text-on-surface">{{ fmtCurrency(personnels.sommes.couts) }}</td>
              </tr>
              <template v-if="personnels.additionnels.length">
                <tr class="bg-primary/5 border-t border-primary/10">
                  <td :colspan="pColFull" class="px-4 py-1.5 text-[10px] font-black uppercase tracking-widest text-primary">Personnel additionnel</td>
                </tr>
                <tr v-for="(p, pi) in personnels.additionnels" :key="'pa'+pi" class="hover:bg-surface-container-highest/20 transition-colors">
                  <td class="px-4 py-2"><span class="font-mono font-bold text-primary">{{ p.code }}</span></td>
                  <td class="px-4 py-2 text-on-surface">{{ p.nom }}</td>
                  <template v-if="show_camionneurs">
                    <td class="px-4 py-2 text-center"><span v-if="p.am" class="material-symbols-outlined text-xs text-tertiary">check</span><span v-else class="text-outline">—</span></td>
                    <td class="px-4 py-2 text-center"><span v-if="p.pm" class="material-symbols-outlined text-xs text-tertiary">check</span><span v-else class="text-outline">—</span></td>
                    <td class="px-4 py-2 text-center"><span v-if="p.inspection" class="material-symbols-outlined text-xs text-tertiary">check</span><span v-else class="text-outline">—</span></td>
                  </template>
                  <td v-if="can_see_costs_personnel" class="px-4 py-2 text-right font-mono text-on-surface">{{ fmtCurrency(p.taux_horaire) }}</td>
                  <td class="px-4 py-2 text-right font-mono text-on-surface">{{ fmtQty(p.quantite_regulier) }}</td>
                  <td class="px-4 py-2 text-right font-mono text-on-surface">{{ fmtQty(p.quantite_supplementaire) }}</td>
                  <td v-if="can_see_costs_personnel" class="px-4 py-2 text-right font-mono text-on-surface">{{ fmtCurrency(p.quantite_taux) }}</td>
                </tr>
                <tr class="bg-surface-container-highest/40">
                  <td :colspan="pColBase" class="px-4 py-1.5 text-[10px] font-black uppercase tracking-widest text-outline text-right">Sous-total</td>
                  <td class="px-4 py-1.5 text-right font-mono font-bold text-on-surface">{{ fmtQty(personnels.additionnel_sommes.regulier) }}</td>
                  <td class="px-4 py-1.5 text-right font-mono font-bold text-on-surface">{{ fmtQty(personnels.additionnel_sommes.supplementaire) }}</td>
                  <td v-if="can_see_costs_personnel" class="px-4 py-1.5 text-right font-mono font-bold text-on-surface">{{ fmtCurrency(personnels.additionnel_sommes.couts) }}</td>
                </tr>
              </template>
              <template v-if="personnels.externes.length">
                <tr class="bg-primary/5 border-t border-primary/10">
                  <td :colspan="pColFull" class="px-4 py-1.5 text-[10px] font-black uppercase tracking-widest text-primary">Personnel externe</td>
                </tr>
                <tr v-for="(p, pi) in personnels.externes" :key="'pe'+pi" class="hover:bg-surface-container-highest/20 transition-colors">
                  <td class="px-4 py-2"><span class="font-mono font-bold text-primary">{{ p.code }}</span></td>
                  <td class="px-4 py-2 text-on-surface">{{ p.nom }}</td>
                  <template v-if="show_camionneurs"><td class="px-4 py-2 text-center text-outline">—</td><td class="px-4 py-2 text-center text-outline">—</td><td class="px-4 py-2 text-center text-outline">—</td></template>
                  <td v-if="can_see_costs_personnel" class="px-4 py-2 text-right font-mono text-on-surface">{{ fmtCurrency(p.taux_horaire) }}</td>
                  <td class="px-4 py-2 text-right font-mono text-on-surface">{{ fmtQty(p.quantite_regulier) }}</td>
                  <td class="px-4 py-2 text-right font-mono text-on-surface">{{ fmtQty(p.quantite_supplementaire) }}</td>
                  <td v-if="can_see_costs_personnel" class="px-4 py-2 text-right font-mono text-on-surface">{{ fmtCurrency(p.quantite_taux) }}</td>
                </tr>
                <tr class="bg-surface-container-highest/40">
                  <td :colspan="pColBase" class="px-4 py-1.5 text-[10px] font-black uppercase tracking-widest text-outline text-right">Sous-total</td>
                  <td class="px-4 py-1.5 text-right font-mono font-bold text-on-surface">{{ fmtQty(personnels.externe_sommes.regulier) }}</td>
                  <td class="px-4 py-1.5 text-right font-mono font-bold text-on-surface">{{ fmtQty(personnels.externe_sommes.supplementaire) }}</td>
                  <td v-if="can_see_costs_personnel" class="px-4 py-1.5 text-right font-mono font-bold text-on-surface">{{ fmtCurrency(personnels.externe_sommes.couts) }}</td>
                </tr>
              </template>
              <template v-if="personnels.additionnels.length || personnels.externes.length">
                <tr class="bg-surface-container-highest border-t-2 border-primary/20">
                  <td :colspan="pColBase" class="px-4 py-2 text-[10px] font-black uppercase tracking-widest text-primary text-right">Grand total</td>
                  <td class="px-4 py-2 text-right font-mono font-black text-on-surface">{{ fmtQty(personnels.grand_total.regulier) }}</td>
                  <td class="px-4 py-2 text-right font-mono font-black text-on-surface">{{ fmtQty(personnels.grand_total.supplementaire) }}</td>
                  <td v-if="can_see_costs_personnel" class="px-4 py-2 text-right font-mono font-black text-on-surface">{{ fmtCurrency(personnels.grand_total.couts) }}</td>
                </tr>
              </template>
            </tbody>
          </table>
        </div>

        <!-- Équipements -->
        <div class="bg-surface-container rounded-xl overflow-hidden shadow-xl border border-outline-variant/10">
          <div class="px-6 py-4 bg-surface-container-high/60 border-b border-outline-variant/15">
            <h2 class="text-[11px] font-black uppercase tracking-widest text-on-surface">Équipements</h2>
          </div>
          <div v-if="!equipements.equipements.length && !equipements.additionnels.length && !equipements.externes.length"
            class="px-6 py-4 text-sm text-outline italic">Aucun équipement</div>
          <table v-else class="w-full text-left border-collapse text-xs">
            <thead>
              <tr class="border-b border-outline-variant/15">
                <th class="px-4 py-3 text-[10px] font-black uppercase tracking-widest text-outline">Code</th>
                <th class="px-4 py-3 text-[10px] font-black uppercase tracking-widest text-outline">Nom</th>
                <th v-if="can_see_costs_personnel" class="px-4 py-3 text-[10px] font-black uppercase tracking-widest text-outline text-right w-20">Taux</th>
                <th class="px-4 py-3 text-[10px] font-black uppercase tracking-widest text-outline text-right w-14">Qté</th>
                <th v-if="can_see_costs_personnel" class="px-4 py-3 text-[10px] font-black uppercase tracking-widest text-outline text-right w-24">Coût</th>
              </tr>
            </thead>
            <tbody class="divide-y divide-outline-variant/10">
              <tr v-for="(e, ei) in equipements.equipements" :key="'e'+ei" class="hover:bg-surface-container-highest/20 transition-colors">
                <td class="px-4 py-2"><span class="font-mono font-bold text-primary">{{ e.code }}</span></td>
                <td class="px-4 py-2 text-on-surface">{{ e.nom }}</td>
                <td v-if="can_see_costs_personnel" class="px-4 py-2 text-right font-mono text-on-surface">{{ fmtCurrency(e.taux_horaire) }}</td>
                <td class="px-4 py-2 text-right font-mono text-on-surface">{{ fmtQty(e.quantite) }}</td>
                <td v-if="can_see_costs_personnel" class="px-4 py-2 text-right font-mono text-on-surface">{{ fmtCurrency(e.quantite_taux) }}</td>
              </tr>
              <tr v-if="equipements.equipements.length" class="bg-surface-container-highest/40 border-t border-outline-variant/20">
                <td :colspan="eColBase" class="px-4 py-1.5 text-[10px] font-black uppercase tracking-widest text-outline text-right">Sous-total</td>
                <td class="px-4 py-1.5 text-right font-mono font-bold text-on-surface">{{ fmtQty(equipements.sommes.quantite) }}</td>
                <td v-if="can_see_costs_personnel" class="px-4 py-1.5 text-right font-mono font-bold text-on-surface">{{ fmtCurrency(equipements.sommes.couts) }}</td>
              </tr>
              <template v-if="equipements.additionnels.length">
                <tr class="bg-primary/5 border-t border-primary/10">
                  <td :colspan="eColFull" class="px-4 py-1.5 text-[10px] font-black uppercase tracking-widest text-primary">Équipement additionnel</td>
                </tr>
                <tr v-for="(e, ei) in equipements.additionnels" :key="'ea'+ei" class="hover:bg-surface-container-highest/20 transition-colors">
                  <td class="px-4 py-2"><span class="font-mono font-bold text-primary">{{ e.code }}</span></td>
                  <td class="px-4 py-2 text-on-surface">{{ e.nom }}</td>
                  <td v-if="can_see_costs_personnel" class="px-4 py-2 text-right font-mono text-on-surface">{{ fmtCurrency(e.taux_horaire) }}</td>
                  <td class="px-4 py-2 text-right font-mono text-on-surface">{{ fmtQty(e.quantite) }}</td>
                  <td v-if="can_see_costs_personnel" class="px-4 py-2 text-right font-mono text-on-surface">{{ fmtCurrency(e.quantite_taux) }}</td>
                </tr>
                <tr class="bg-surface-container-highest/40">
                  <td :colspan="eColBase" class="px-4 py-1.5 text-[10px] font-black uppercase tracking-widest text-outline text-right">Sous-total</td>
                  <td class="px-4 py-1.5 text-right font-mono font-bold text-on-surface">{{ fmtQty(equipements.additionnel_sommes.quantite) }}</td>
                  <td v-if="can_see_costs_personnel" class="px-4 py-1.5 text-right font-mono font-bold text-on-surface">{{ fmtCurrency(equipements.additionnel_sommes.couts) }}</td>
                </tr>
              </template>
              <template v-if="equipements.externes.length">
                <tr class="bg-primary/5 border-t border-primary/10">
                  <td :colspan="eColFull" class="px-4 py-1.5 text-[10px] font-black uppercase tracking-widest text-primary">Équipement externe</td>
                </tr>
                <tr v-for="(e, ei) in equipements.externes" :key="'ee'+ei" class="hover:bg-surface-container-highest/20 transition-colors">
                  <td class="px-4 py-2"><span class="font-mono font-bold text-primary">{{ e.code }}</span></td>
                  <td class="px-4 py-2 text-on-surface">{{ e.nom }}</td>
                  <td v-if="can_see_costs_personnel" class="px-4 py-2 text-right font-mono text-on-surface">{{ fmtCurrency(e.taux_horaire) }}</td>
                  <td class="px-4 py-2 text-right font-mono text-on-surface">{{ fmtQty(e.quantite) }}</td>
                  <td v-if="can_see_costs_personnel" class="px-4 py-2 text-right font-mono text-on-surface">{{ fmtCurrency(e.quantite_taux) }}</td>
                </tr>
                <tr class="bg-surface-container-highest/40">
                  <td :colspan="eColBase" class="px-4 py-1.5 text-[10px] font-black uppercase tracking-widest text-outline text-right">Sous-total</td>
                  <td class="px-4 py-1.5 text-right font-mono font-bold text-on-surface">{{ fmtQty(equipements.externe_sommes.quantite) }}</td>
                  <td v-if="can_see_costs_personnel" class="px-4 py-1.5 text-right font-mono font-bold text-on-surface">{{ fmtCurrency(equipements.externe_sommes.couts) }}</td>
                </tr>
              </template>
              <template v-if="equipements.additionnels.length || equipements.externes.length">
                <tr class="bg-surface-container-highest border-t-2 border-primary/20">
                  <td :colspan="eColBase" class="px-4 py-2 text-[10px] font-black uppercase tracking-widest text-primary text-right">Grand total</td>
                  <td class="px-4 py-2 text-right font-mono font-black text-on-surface">{{ fmtQty(equipements.grand_total.quantite) }}</td>
                  <td v-if="can_see_costs_personnel" class="px-4 py-2 text-right font-mono font-black text-on-surface">{{ fmtCurrency(equipements.grand_total.couts) }}</td>
                </tr>
              </template>
            </tbody>
          </table>
        </div>
      </div>

      <!-- ── Inventaires ─────────────────────────────────────────────────── -->
      <div v-if="show_inventaire && inventaires" class="mb-5">
        <div class="bg-surface-container rounded-xl overflow-hidden shadow-xl border border-outline-variant/10">
          <div class="px-6 py-4 bg-surface-container-high/60 border-b border-outline-variant/15">
            <h2 class="text-[11px] font-black uppercase tracking-widest text-on-surface">Inventaires</h2>
          </div>
          <table class="w-full text-left border-collapse text-xs">
            <thead>
              <tr class="border-b border-outline-variant/15">
                <th class="px-6 py-3 text-[10px] font-black uppercase tracking-widest text-outline w-32">Code</th>
                <th class="px-6 py-3 text-[10px] font-black uppercase tracking-widest text-outline">Nom</th>
                <th class="px-6 py-3 text-[10px] font-black uppercase tracking-widest text-outline text-right w-24">Quantité</th>
              </tr>
            </thead>
            <tbody class="divide-y divide-outline-variant/10">
              <tr v-for="(inv, ii) in inventaires.inventaires" :key="'inv'+ii" class="hover:bg-surface-container-highest/20 transition-colors">
                <td class="px-6 py-3"><span class="font-mono font-bold text-primary">{{ inv.code }}</span></td>
                <td class="px-6 py-3 text-on-surface">{{ inv.nom }}</td>
                <td class="px-6 py-3 text-right font-mono text-on-surface">{{ fmtQty(inv.quantite) }}</td>
              </tr>
              <tr v-if="inventaires.additionnels.length">
                <td colspan="3" class="px-6 py-1.5 text-[10px] font-black uppercase tracking-widest text-primary bg-primary/5">Inventaire additionnel</td>
              </tr>
              <tr v-for="(inv, ii) in inventaires.additionnels" :key="'inva'+ii" class="hover:bg-surface-container-highest/20 transition-colors">
                <td class="px-6 py-3"><span class="font-mono font-bold text-primary">{{ inv.code }}</span></td>
                <td class="px-6 py-3 text-on-surface">{{ inv.nom }}</td>
                <td class="px-6 py-3 text-right font-mono text-on-surface">{{ fmtQty(inv.quantite) }}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      <!-- ── Parties Suivies ─────────────────────────────────────────────── -->
      <div v-if="show_partie_suivies && parties_suivies" class="mb-5">
        <div v-for="partie in parties_suivies.parties" :key="partie.code" class="mb-4">
          <div class="bg-surface-container rounded-xl overflow-hidden shadow-xl border border-outline-variant/10">
            <div class="px-6 py-4 bg-surface-container-high/60 border-b border-outline-variant/15 flex items-center justify-between">
              <h2 class="text-[11px] font-black uppercase tracking-widest text-on-surface">{{ partie.type_description || partie.code }}</h2>
              <span v-if="can_see_costs && partie.elements_couts" class="font-mono font-bold text-sm text-primary">{{ fmtCurrency(partie.elements_couts) }}</span>
            </div>
            <table v-if="partie.elements.length" class="w-full text-left border-collapse text-xs">
              <thead>
                <tr class="border-b border-outline-variant/15">
                  <th class="px-6 py-3 text-[10px] font-black uppercase tracking-widest text-outline w-24">Code</th>
                  <th class="px-6 py-3 text-[10px] font-black uppercase tracking-widest text-outline">Description</th>
                  <th class="px-6 py-3 text-[10px] font-black uppercase tracking-widest text-outline text-right w-24">Quantité</th>
                  <th v-if="can_see_costs" class="px-6 py-3 text-[10px] font-black uppercase tracking-widest text-outline text-right w-24">Taux unit.</th>
                  <th v-if="can_see_costs" class="px-6 py-3 text-[10px] font-black uppercase tracking-widest text-outline text-right w-24">Coût</th>
                </tr>
              </thead>
              <tbody class="divide-y divide-outline-variant/10">
                <tr v-for="(el, eli) in partie.elements" :key="eli" class="hover:bg-surface-container-highest/20 transition-colors">
                  <td class="px-6 py-3"><span class="font-mono font-bold text-primary">{{ el.code }}</span></td>
                  <td class="px-6 py-3 text-on-surface">{{ el.description }}</td>
                  <td class="px-6 py-3 text-right font-mono text-on-surface">{{ fmtQty(el.quantite) }} <span class="text-outline text-[10px]">{{ el.unite }}</span></td>
                  <td v-if="can_see_costs" class="px-6 py-3 text-right font-mono text-on-surface">{{ fmtCurrency(el.taux_unitaire) }}</td>
                  <td v-if="can_see_costs" class="px-6 py-3 text-right font-mono text-on-surface">{{ fmtCurrency(el.couts) }}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>

      <!-- ── Liaisons ────────────────────────────────────────────────────── -->
      <div v-if="show_liaisons && liaisons" class="mb-5">
        <div class="bg-surface-container rounded-xl overflow-hidden shadow-xl border border-outline-variant/10">
          <div class="px-6 py-4 bg-surface-container-high/60 border-b border-outline-variant/15 flex items-center justify-between gap-4">
            <h2 class="text-[11px] font-black uppercase tracking-widest text-on-surface">Liaisons</h2>
            <a v-if="can_update" :href="rapport.edit_liaisons_url"
               class="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold rounded-lg bg-primary/10 text-primary hover:bg-primary/20 transition-colors">
              <span class="material-symbols-outlined text-sm">edit</span>Modifier
            </a>
          </div>
          <div class="p-6 space-y-4">
            <template v-if="liaisons.length">
              <div v-for="liaison in liaisons" :key="liaison.description">
                <div class="text-[10px] font-black uppercase tracking-widest text-primary mb-2">{{ liaison.description }}</div>
                <ul class="space-y-1">
                  <li v-for="(item, ii) in liaison.items" :key="ii" class="text-sm text-on-surface pl-3 border-l-2 border-primary/30">{{ item }}</li>
                  <li v-if="!liaison.items.length" class="text-sm text-outline italic">Aucune entrée</li>
                </ul>
              </div>
            </template>
            <p v-else class="text-sm text-outline italic">Aucune entrée</p>
          </div>
        </div>
      </div>

      <!-- ── Liaisons texte ─────────────────────────────────────────────── -->
      <template v-if="show_liaisons && liaisons_texte">
        <div v-for="lt in liaisons_texte" :key="lt.description" class="mb-5">
          <div class="bg-surface-container rounded-xl overflow-hidden shadow-xl border border-outline-variant/10">
            <div class="px-6 py-4 bg-surface-container-high/60 border-b border-outline-variant/15 flex items-center justify-between gap-4">
              <h2 class="text-[11px] font-black uppercase tracking-widest text-on-surface">{{ lt.description }}</h2>
              <a v-if="can_update" :href="rapport.edit_liaisons_url"
                 class="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold rounded-lg bg-primary/10 text-primary hover:bg-primary/20 transition-colors">
                <span class="material-symbols-outlined text-sm">edit</span>Modifier
              </a>
            </div>
            <div class="p-6">
              <ul v-if="lt.items.length" class="space-y-2">
                <li v-for="(item, ii) in lt.items" :key="ii"
                  class="flex items-start gap-2 text-sm text-on-surface">
                  <span class="material-symbols-outlined text-sm text-outline shrink-0 mt-0.5">description</span>
                  {{ item }}
                </li>
              </ul>
              <p v-else class="text-sm text-outline italic">Aucune entrée</p>
            </div>
          </div>
        </div>
      </template>

      <!-- ── Commentaires ────────────────────────────────────────────────── -->
      <div class="mb-5">
        <div class="bg-surface-container rounded-xl overflow-hidden shadow-xl border border-outline-variant/10">
          <div class="px-6 py-4 bg-surface-container-high/60 border-b border-outline-variant/15">
            <h2 class="text-[11px] font-black uppercase tracking-widest text-on-surface">Commentaires</h2>
          </div>
          <div class="p-6">
            <div v-if="!rapport.commentaires || !rapport.commentaires.trim()" class="text-sm text-outline italic">Aucun commentaire</div>
            <pre v-else class="text-sm text-on-surface-variant whitespace-pre-wrap font-sans leading-relaxed">{{ rapport.commentaires }}</pre>
          </div>
        </div>
      </div>

      <!-- ── Images ─────────────────────────────────────────────────────── -->
      <div v-if="rapport.images && rapport.images.length" class="mb-5">
        <div class="bg-surface-container rounded-xl overflow-hidden shadow-xl border border-outline-variant/10">
          <div class="px-6 py-4 bg-surface-container-high/60 border-b border-outline-variant/15">
            <h2 class="text-[11px] font-black uppercase tracking-widest text-on-surface">
              Images
              <span class="ml-2 text-outline/50">({{ rapport.images.length }})</span>
            </h2>
          </div>
          <div class="p-6 grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4">
            <a
              v-for="img in rapport.images"
              :key="img.id"
              :href="img.url"
              target="_blank"
              rel="noopener"
              class="group block space-y-2"
            >
              <div class="aspect-square rounded-lg overflow-hidden bg-surface-container-high border border-outline-variant/10 group-hover:border-primary/30 transition-colors">
                <img
                  :src="img.thumbnail_url || img.url"
                  :alt="img.filename || 'Image'"
                  class="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                />
              </div>
              <div v-if="img.description" class="text-[10px] text-outline leading-tight px-0.5">
                {{ img.description }}
              </div>
              <div v-if="img.created_at" class="text-[10px] text-outline/60 px-0.5">{{ img.created_at }}</div>
            </a>
          </div>
        </div>
      </div>

    </div>
  </AppLayout>
</template>
